/**
 * Web トラッキング（タスク48）。ログイン中のユーザーの位置を一定間隔で送り、地図のピンを動かす。
 *
 * Telegram のライブロケーション相当を Web で行う。Amazon Location の Trackers は使わず、
 * ブラウザの watchPosition で位置を受け取り、既存の POST /api/checkin（source=web-gps）に送る
 * （保存先・軌跡・公開トグル・60分 TTL は既存のまま）。
 *
 * - ブラウザは画面を開いている間だけ送れる（タブを閉じる・別アプリ・画面オフで止まる）。
 * - 送信間隔は Telegram のライブロケーションと同程度の30秒。
 * - 開始時に 15分／1時間／8時間／停止するまで を選ぶ。期限で自動停止、ログアウトでも停止。
 * - 再読み込み後も、同じユーザーで期限内なら自動で再開する（localStorage）。
 * - 動作中は Screen Wake Lock で画面を消さない（非対応ブラウザでは何もしない）。
 *
 * メニュー内のコントロールはモーダルを閉じるとアンマウントされる（タスク39）ため、
 * 状態と送信ループは useAuth と同じく App で一度だけ持つ。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { postCheckin } from '../api/client';
import { loadTokens } from './cognito';
import type { UseAuthResult } from './useAuth';

export type TrackingDuration = '15m' | '1h' | '8h' | 'indefinite';

/** 選べる時間（秒）。indefinite は停止するまで。 */
export const TRACKING_DURATIONS: Record<TrackingDuration, number | null> = {
  '15m': 15 * 60,
  '1h': 60 * 60,
  '8h': 8 * 60 * 60,
  indefinite: null,
};

/** 送信間隔（Telegram のライブロケーションの実測と同程度）。 */
const SEND_INTERVAL_MS = 30_000;
/** 自動停止・送信時刻の判定の刻み（5秒刻みだと実測で約35秒間隔になったため1秒・タスク49）。 */
const TICK_MS = 1_000;
/** これより古い位置は送らず、取り直してから送る（画面オフ中の古い位置を「今」として送らない）。 */
const MAX_FIX_AGE_MS = 30_000;
const STORAGE_KEY = 'tracking';

/**
 * 動作に問題があったときの理由。
 * - denied / session: 続けられないので停止済み
 * - network / position: 続行中（次の送信で回復しうる）
 */
export type TrackingProblem = 'denied' | 'session' | 'network' | 'position';

export interface UseTrackingResult {
  readonly active: boolean;
  /** 終了予定（epoch ミリ秒）。null は停止するまで。 */
  readonly endsAt: number | null;
  /** 最後に送れた時刻（epoch ミリ秒）。 */
  readonly lastSentAt: number | null;
  readonly problem: TrackingProblem | null;
  /** 開始する。開始前に取れた現在地があれば渡す（最初の1回目としてすぐ送る・タスク49）。 */
  readonly start: (
    duration: TrackingDuration,
    initialFix?: { lat: number; lng: number },
  ) => void;
  readonly stop: () => void;
}

interface StoredTracking {
  sub: string;
  endsAt: number | null;
}

function readStored(): StoredTracking | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredTracking) : null;
  } catch {
    return null;
  }
}

function writeStored(value: StoredTracking | null): void {
  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // 保存できなくても動作は続ける（再読み込み後の再開ができないだけ）
  }
}

interface Fix {
  lat: number;
  lng: number;
  /** 取得時刻（epoch ミリ秒） */
  at: number;
}

export function useTracking(auth: UseAuthResult): UseTrackingResult {
  const [active, setActive] = useState(false);
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const [problem, setProblem] = useState<TrackingProblem | null>(null);

  const sub = auth.user?.sub ?? null;
  /** start で渡された開始前の現在地（送信ループに渡すまで一時的に持つ）。 */
  const initialFixRef = useRef<Fix | null>(null);

  /** 停止する。理由があれば残す（モーダルで表示する）。 */
  const stopWith = useCallback((reason: TrackingProblem | null) => {
    setActive(false);
    setEndsAt(null);
    setProblem(reason);
    writeStored(null);
  }, []);

  const stop = useCallback(() => stopWith(null), [stopWith]);

  const start = useCallback(
    (duration: TrackingDuration, initialFix?: { lat: number; lng: number }) => {
      if (!sub) {
        return;
      }
      initialFixRef.current = initialFix ? { ...initialFix, at: Date.now() } : null;
      const sec = TRACKING_DURATIONS[duration];
      const until = sec === null ? null : Date.now() + sec * 1000;
      setEndsAt(until);
      setLastSentAt(null);
      setProblem(null);
      setActive(true);
      writeStored({ sub, endsAt: until });
    },
    [sub],
  );

  // ログイン状態に合わせて再開・停止する。
  // 起動時（再読み込み後）に同じユーザーで期限内なら再開。ログアウト・別ユーザーなら止めて記録も消す。
  useEffect(() => {
    if (auth.loading) {
      return;
    }
    if (!sub) {
      if (active) {
        stopWith(null);
      } else {
        writeStored(null);
      }
      return;
    }
    if (active) {
      return;
    }
    const stored = readStored();
    if (!stored) {
      return;
    }
    if (stored.sub !== sub || (stored.endsAt !== null && stored.endsAt <= Date.now())) {
      writeStored(null);
      return;
    }
    setEndsAt(stored.endsAt);
    setLastSentAt(null);
    setProblem(null);
    setActive(true);
  }, [auth.loading, sub, active, stopWith]);

  // 動作中の位置の受信・定期送信・自動停止・Wake Lock。
  const endsAtRef = useRef<number | null>(null);
  endsAtRef.current = endsAt;

  useEffect(() => {
    if (!active) {
      return;
    }
    if (!navigator.geolocation) {
      stopWith('position');
      return;
    }

    let disposed = false;
    // 開始前に取れた現在地があれば、それを最初の位置にしてすぐ送る（タスク49）
    let fix: Fix | null = initialFixRef.current;
    initialFixRef.current = null;
    /** 間隔の起点（送信を始めた時刻）。完了時刻を起点にすると通信・取り直しの分だけ間隔が伸びる（タスク49）。 */
    let lastAttempt = 0;
    let sending = false;
    let wakeLock: WakeLockSentinel | null = null;

    const onPosition = (pos: GeolocationPosition) => {
      fix = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: Date.now() };
      // 開始直後は最初の位置が取れた時点ですぐ送る
      if (lastAttempt === 0) {
        void send();
      }
    };
    const onPositionError = (err: GeolocationPositionError) => {
      console.warn('tracking geolocation failed', err.code, err.message);
      if (err.code === err.PERMISSION_DENIED) {
        stopWith('denied');
      } else {
        // 位置を特定できない・タイムアウトは一時的なことが多いので続ける
        setProblem('position');
      }
    };

    /** 送信のタイミングで位置が古ければ取り直す（止まっている人のピンを古くしない）。 */
    const freshFix = (): Promise<Fix | null> => {
      if (fix && Date.now() - fix.at <= MAX_FIX_AGE_MS) {
        return Promise.resolve(fix);
      }
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            onPosition(pos);
            resolve(fix);
          },
          (err) => {
            onPositionError(err);
            resolve(null);
          },
          { enableHighAccuracy: true, maximumAge: MAX_FIX_AGE_MS, timeout: 15_000 },
        );
      });
    };

    const send = async () => {
      if (sending || disposed) {
        return;
      }
      sending = true;
      lastAttempt = Date.now();
      try {
        const current = await freshFix();
        if (!current || disposed) {
          return;
        }
        const token = loadTokens()?.id_token;
        if (!token) {
          stopWith('session');
          return;
        }
        await postCheckin(token, {
          lat: current.lat,
          lng: current.lng,
          source: 'web-gps',
        });
        if (disposed) {
          return;
        }
        setLastSentAt(Date.now());
        setProblem(null);
      } catch (err) {
        if (err instanceof Error && err.message.includes('401')) {
          stopWith('session');
        } else {
          console.warn('tracking send failed', err);
          setProblem('network');
        }
      } finally {
        sending = false;
      }
    };

    const tick = () => {
      const until = endsAtRef.current;
      if (until !== null && Date.now() >= until) {
        stopWith(null);
        return;
      }
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (Date.now() - lastAttempt >= SEND_INTERVAL_MS) {
        void send();
      }
    };

    const acquireWakeLock = async () => {
      if (!('wakeLock' in navigator) || document.visibilityState !== 'visible') {
        return;
      }
      try {
        wakeLock = await navigator.wakeLock.request('screen');
      } catch {
        // 省電力モード等で拒否されることがある。画面が消えたら送信が止まるだけなので無視する
      }
    };
    // 画面が隠れると Wake Lock は自動で解除されるので、戻ったら取り直す。
    // 戻った直後は次の tick（5秒以内）で送られる。
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void acquireWakeLock();
      }
    };

    const watchId = navigator.geolocation.watchPosition(
      onPosition,
      onPositionError,
      { enableHighAccuracy: true, maximumAge: 10_000 },
    );
    const timer = setInterval(tick, TICK_MS);
    if (fix) {
      void send();
    }
    document.addEventListener('visibilitychange', onVisibility);
    void acquireWakeLock();

    return () => {
      disposed = true;
      navigator.geolocation.clearWatch(watchId);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      void wakeLock?.release().catch(() => undefined);
    };
  }, [active, stopWith]);

  return { active, endsAt, lastSentAt, problem, start, stop };
}
